import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { examApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ExamResultAnalysis from '../components/ExamResultAnalysis';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import './ExamResultsPage.css';
import { formatDate } from '../utils/format';
import { chartTheme } from '../styles/chartTheme';

interface ExamResultsPageProps {
  sessionId: string;
  onBack: () => void;
  userId?: string;
}

interface ResultQuestion {
  question_id: string;
  question_order: number;
  question_text: string;
  options: Array<{ option_number: number; option_text: string }>;
  user_answers: number[];
  correct_answers: number[];
  is_correct: boolean;
  answered_at?: string;
  explanation: string;
  reasoning: string;
}

interface ExamResults {
  sessionId: string;
  score: number;
  percentage: number;
  totalQuestions: number;
  correctAnswers: number;
  answeredQuestions: number;
  timeTakenMinutes: number;
  passed: boolean;
  startedAt: string;
  completedAt: string;
  studySummary?: string;
  questions: ResultQuestion[];
}

const ExamResultsPage: React.FC<ExamResultsPageProps> = ({ sessionId, onBack }) => {
  const [results, setResults] = useState<ExamResults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const { user } = useAuth();

  useEffect(() => {
    const loadResults = async () => {
      try {
        if (user?.is_admin) {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/exam/${sessionId}/detailed-results`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
          });
          const data = await response.json();
          if (data.success) {
            setResults(data.data as ExamResults);
          } else {
            setError('결과를 불러올 수 없습니다.');
          }
        } else {
          const response = await examApi.getResults(sessionId);
          if (response.success) {
            setResults(response.data as ExamResults);
          } else {
            setError('결과를 불러올 수 없습니다.');
          }
        }
      } catch {
        setError('결과를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadResults();
  }, [sessionId, user]);

  const toggleQuestion = (questionOrder: number) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionOrder)) {
        next.delete(questionOrder);
      } else {
        next.add(questionOrder);
      }
      return next;
    });
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}시간 ${mins}분` : `${mins}분`;
  };

  const filteredQuestions = useMemo(() => {
    if (!results) return [];
    return results.questions.filter((q) => {
      if (filter === 'correct') return q.is_correct;
      if (filter === 'incorrect') return !q.is_correct;
      return true;
    });
  }, [results, filter]);

  const expandAll = () => {
    setExpandedQuestions(new Set(filteredQuestions.map((q) => q.question_order)));
  };

  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  const formatAnswerList = (answers: number[]) => {
    if (!answers || answers.length === 0) return '-';
    return answers.join(', ');
  };

  const gaugeOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: 'radialBar',
      sparkline: { enabled: true }
    },
    plotOptions: {
      radialBar: {
        startAngle: -140,
        endAngle: 140,
        hollow: {
          size: '68%'
        },
        track: {
          background: chartTheme.track,
          strokeWidth: '100%',
          margin: 0
        },
        dataLabels: {
          name: {
            show: true,
            offsetY: 24,
            color: chartTheme.body,
            fontSize: '13px',
            fontWeight: 700
          },
          value: {
            offsetY: -10,
            color: chartTheme.ink,
            fontSize: '34px',
            fontWeight: 800,
            formatter: (val) => `${Math.round(val)}%`
          }
        }
      }
    },
    labels: [results?.passed ? '합격' : '불합격'],
    colors: [results?.passed ? chartTheme.success : chartTheme.danger],
    fill: { type: 'solid' },
    stroke: {
      lineCap: 'round'
    }
  }), [results?.passed]);

  if (isLoading) {
    return (
      <div className="results-v3 results-v3--loading">
        <div className="results-v3__spinner"></div>
        <p>시험 결과를 불러오는 중...</p>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="results-v3">
        <Card padding="lg" shadow="md">
          <div className="results-v3__error-box">
            <h3>결과 조회 실패</h3>
            <p>{error || '결과를 찾을 수 없습니다.'}</p>
            <Button variant="primary" onClick={onBack}>돌아가기</Button>
          </div>
        </Card>
      </div>
    );
  }

  const correctCount = results.questions.filter((q) => q.is_correct).length;
  const incorrectCount = results.questions.length - correctCount;
  const passingScore = 72;
  const clampedScore = Math.max(0, Math.min(100, results.percentage));

  return (
    <div className="results-v3">
      <section className="results-v3__hero">
        <div className="results-v3__hero-top">
          <Button variant="outline-secondary" onClick={onBack}>← 대시보드</Button>
          <span className={`results-v3__status ${results.passed ? 'passed' : 'failed'}`}>
            {results.passed ? 'PASS' : 'FAIL'}
          </span>
        </div>
        <h1>시험 결과 리포트</h1>
        <p>핵심 지표와 문항별 해설을 빠르게 확인하고 다음 학습 우선순위를 정리하세요.</p>
      </section>

      <div className="results-v3__layout">
        <aside className="results-v3__sidebar">
          <Card className="results-v3__score-card" padding="lg" shadow="md">
            <div className="results-v3__score-label">총점</div>
            <div className="results-v3__gauge-wrap">
              <div className="results-v3__gauge-chart">
                <ReactApexChart
                  type="radialBar"
                  options={gaugeOptions}
                  series={[clampedScore]}
                  height={176}
                />
              </div>
              <div className="results-v3__pass-line">
                <span>합격선 {passingScore}%</span>
              </div>
            </div>
            <div className="results-v3__score-meta">
              <span>{results.correctAnswers}/{results.totalQuestions} 정답</span>
              <span>{formatTime(results.timeTakenMinutes)}</span>
            </div>
          </Card>

          <Card className="results-v3__mini-grid" padding="md" shadow="sm">
            <div className="results-v3__mini-item">
              <span>정답</span>
              <strong>{correctCount}</strong>
            </div>
            <div className="results-v3__mini-item">
              <span>오답</span>
              <strong>{incorrectCount}</strong>
            </div>
            <div className="results-v3__mini-item">
              <span>응답</span>
              <strong>{results.answeredQuestions}</strong>
            </div>
            <div className="results-v3__mini-item">
              <span>제출</span>
              <strong>{formatDate(results.completedAt)}</strong>
            </div>
          </Card>

          {results.studySummary && (
            <Card className="results-v3__summary" padding="md" shadow="sm" title="학습 요약">
              <p>{results.studySummary}</p>
            </Card>
          )}

          {(user?.paid_plan ?? 0) > 0 && (
            <ExamResultAnalysis
              questions={results.questions}
              totalQuestions={results.totalQuestions}
              correctAnswers={results.correctAnswers}
              score={results.percentage}
            />
          )}
        </aside>

        <main className="results-v3__content">
          <Card padding="lg" shadow="md" className="results-v3__questions-wrap">
            <div className="results-v3__questions-head">
              <h2>문항별 상세 결과</h2>
              <div className="results-v3__head-actions">
                <button type="button" onClick={expandAll}>전체 펼치기</button>
                <button type="button" onClick={collapseAll}>전체 닫기</button>
              </div>
            </div>

            <div className="results-v3__filters">
              <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>
                전체 {results.questions.length}
              </button>
              <button className={filter === 'correct' ? 'active' : ''} onClick={() => setFilter('correct')}>
                정답 {correctCount}
              </button>
              <button className={filter === 'incorrect' ? 'active' : ''} onClick={() => setFilter('incorrect')}>
                오답 {incorrectCount}
              </button>
            </div>

            <div className="results-v3__quick-table-wrap">
              <table className="results-v3__quick-table">
                <thead>
                  <tr>
                    <th>문항</th>
                    <th>내 답</th>
                    <th>정답</th>
                    <th>결과</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.map((question) => (
                    <tr key={`quick-${question.question_order}`}>
                      <td>{question.question_order}</td>
                      <td>{formatAnswerList(question.user_answers)}</td>
                      <td>{formatAnswerList(question.correct_answers)}</td>
                      <td>
                        <span className={`results-v3__badge ${question.is_correct ? 'correct' : 'incorrect'}`}>
                          {question.is_correct ? '정답' : '오답'}
                        </span>
                      </td>
                      <td className="results-v3__quick-action">
                        <button type="button" onClick={() => toggleQuestion(question.question_order)}>
                          {expandedQuestions.has(question.question_order) ? '닫기' : '펼치기'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="results-v3__question-list">
              {filteredQuestions.map((question) => {
                const expanded = expandedQuestions.has(question.question_order);
                return (
                  <article
                    key={question.question_order}
                    className="results-v3__question-item"
                    data-question-order={question.question_order}
                  >
                    <button
                      className="results-v3__question-header"
                      onClick={() => toggleQuestion(question.question_order)}
                    >
                      <div className="results-v3__question-head-left">
                        <span className="results-v3__q-number">문제 {question.question_order}</span>
                        <span className={`results-v3__badge ${question.is_correct ? 'correct' : 'incorrect'}`}>
                          {question.is_correct ? '정답' : '오답'}
                        </span>
                      </div>
                      <span className="results-v3__expand">{expanded ? '접기 −' : '펼치기 +'}</span>
                    </button>

                    {expanded && (
                      <div className="results-v3__question-body">
                        <p className="results-v3__question-text">{question.question_text}</p>

                        <div className="results-v3__options">
                          {question.options.map((option) => {
                            const isUserAnswer = question.user_answers.includes(option.option_number);
                            const isCorrectAnswer = question.correct_answers.includes(option.option_number);

                            let optionClass = 'results-v3__option';
                            if (isCorrectAnswer) optionClass += ' correct-answer';
                            if (isUserAnswer && !isCorrectAnswer) optionClass += ' wrong-answer';
                            if (isUserAnswer && isCorrectAnswer) optionClass += ' correct-user-answer';

                            return (
                              <div key={option.option_number} className={optionClass}>
                                <span className="results-v3__option-no">{option.option_number}</span>
                                <span className="results-v3__option-text">{option.option_text}</span>
                              </div>
                            );
                          })}
                        </div>

                        <div className="results-v3__explain">
                          <h4>설명</h4>
                          <p>{question.explanation}</p>
                          <h4>해설</h4>
                          <p>{question.reasoning}</p>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default ExamResultsPage;
